import React, { useState, useRef } from "react";
import { Tooltip, theme, Modal } from "antd";
import { MinusOutlined, PlusCircleFilled, EyeOutlined, BgColorsOutlined, EditOutlined, StopFilled, ExclamationCircleOutlined } from "@ant-design/icons";
import ColorButton from "../../common/ColorButton";
import { dateToHumanString, timeToString, toFixed } from "../../frameworks/Util";
import TSTable from "../../common/TSTable";
import { GRAY_COLOR, LAB_COLOR, PRIMARY_COLOR } from "../../constants/color";
import ErrorMessages from "../../common/ErrorMessages";
import AddLotSkimModal from "../../components/storage-skim/AddLotSkimModal";
import CloseLotSkimModal from "./CloseLotSkimModal";
import CoagulationSkimModal from "./CoagulationSkimModal";
import LotSkimWeightPopover from "./LotSkimWeightPopover";
import LotSkimHistoryModal from "./LotSkimHistoryModal";
import { URL_SKIM } from "../../constants/urls";
import { POST } from "../../frameworks/HttpClient";
import { SKIM_STATUS, SKIM_STATUS_TH } from "../../constants/string";
import _ from 'lodash';
import SkimChemicalLogView from "./SkimChemicalLogView";

export default function LotSkimList() {
  const tableRef = useRef(null);
  const [openAddLotSkimModal, setOpenAddLotSkimModal] = useState(false);
  const [openCloseLotSkimModal, setOpenCloseLotSkimModal] = useState(false);
  const [openCoagulationSkimModal, setOpenCoagulationSkimModal] = useState(false);
  const [openLotSkimHistoryModal, setOpenLotSkimHistoryModal] = React.useState(false);
  const [target, setTarget] = useState(null);
  const [skimChemicalLogTarget, setSkimChemicalLogTarget] = useState(null);
  const [errorMessages, setErrorMessages] = useState(null);
  const { token: { colorWarning, colorSuccess }, } = theme.useToken();

  const refreshData = async () => {
    if (tableRef.current) {
      setErrorMessages(null);
      try {
        await tableRef.current.fetchWithoutResetPage();
      } catch (error) { 
        setErrorMessages(error.errorMessages);
      }
    }
  };

  const handleEndWork = async(id) => {
    try {
      const data = {
        action : 'end'
      }
      await POST(`${URL_SKIM.SKIM_TRANSACTION}${id}/change-state/`, data)
      refreshData();
    } catch (error) {
      setErrorMessages(error.errorMessages)
    }
  }

  const confirmEnd = (title, value) => {
    Modal.confirm({
      title: `จบการทำงาน - ${title}`,
      icon: <ExclamationCircleOutlined style={{ color : PRIMARY_COLOR}} />,
      content: 'คุณต้องการจบการทำงานบ่อใช่หรือไม่ ?',
      okText: 'ยืนยัน',
      cancelText: 'ปิด',
      maskClosable: true,
      okButtonProps: {style: { backgroundColor : PRIMARY_COLOR}},
      onOk:() => {handleEndWork(value)},
      onConfirm: () => refreshData()
    });
  };

  const columns = [
    {
      title: "Action",
      dataIndex: "",
      key: "action",
      align: "center",
      width: 100,
      render: (_, record) =>
        (
          <div onClick={(e) => e.stopPropagation()}>
            <Tooltip triger='hover' title="ประวัติเดินเครื่อง"> 
              <ColorButton
                type="primary"
                override={PRIMARY_COLOR}
                icon={<EyeOutlined />}
                style={{ margin : 3 }}
                onClick={() => {
                  setOpenLotSkimHistoryModal(true);
                  setTarget(record);
                }}
              /> 
            </Tooltip>
            {record.status && record.status != SKIM_STATUS.END && (
              <Tooltip triger='hover' title="แก้ไข"> 
                <ColorButton
                  type="primary"
                  override={colorWarning}
                  icon={<EditOutlined />}
                  style={{ margin : 3 }}
                  onClick={() => {
                    setOpenAddLotSkimModal(true);
                    setTarget(record);
                  }}
                /> 
              </Tooltip>
            )}
            {record.status === SKIM_STATUS.FILLING && (
              <Tooltip triger='hover' title="หยุดเติมน้ำยาง"> 
                <ColorButton
                  type="primary"
                  override={GRAY_COLOR}
                  icon={<MinusOutlined />}
                  style={{ margin : 3 }}
                  onClick={() => {
                    setOpenCloseLotSkimModal(true)
                    setTarget(record)
                  }}
                />
              </Tooltip>
            )}
            {record.status === SKIM_STATUS.CLOSED && (
              <Tooltip triger='hover' title="บ่อจับตัว"> 
                <ColorButton
                  type="primary"
                  override={LAB_COLOR}
                  icon={<BgColorsOutlined />}
                  style={{ margin : 3 }}
                  onClick={() => {
                    setOpenCoagulationSkimModal(true)
                    setTarget(record)
                  }}
                />
              </Tooltip>
            )}
            {record.status === SKIM_STATUS.CLOSED && (
              <Tooltip triger='hover' title={"จบการทำงาน"}>
                <ColorButton 
                  type='primary'
                  override={GRAY_COLOR}
                  style={{ margin : 3 }}
                  icon={<StopFilled />}
                  onClick={()=> confirmEnd(record.lot_no, record.id)} />
              </Tooltip>
            )}
          </div>
        ),
    },
    {
      title: "Lot No.",
      dataIndex: "lot_no",
      key: "lot_no",
      searcher : true,
    },
    {
      title: "บ่อ",
      dataIndex: "ponds_name",
      key: "ponds_name",
    },
    {
      title: "ลู่",
      dataIndex: "tracks_name",
      key: "tracks_name",
    },
    {
      title: "วันที่เริ่ม",
      dataIndex: "start_datetime",
      key: "start_datetime",
      align: "center",
      render : (value) => value ?
        <div>
          {dateToHumanString(value)}<br/>
          {timeToString(value)}
        </div> : '-'
    },
    {
      title: "วันที่จบ",
      dataIndex: "end_datetime",
      key: "end_datetime",
      align: "center",
      render : (value) => value ?
      <div>
          {dateToHumanString(value)}<br/>
          {timeToString(value)}
      </div> : '-'
    },
    {
      title: "วันที่จับตัว",
      dataIndex: "last_thicken_date",
      key: "last_thicken_date",
      align: "center",
      render : (value, record) => <Tooltip title={"ประวัติเติมสารเคมี"}>
        { value ? <a onClick={() => setSkimChemicalLogTarget(record.id)}>
          {dateToHumanString(value)}<br/>
          {timeToString(value)}
        </a> : '-'}
      </Tooltip>
    },
    {
      title: "น้ำหนัก",
      dataIndex: "current_weight",
      key: "current_weight",
      align: "center",
      render : (value, original) => <div>
        {value ? value : "-"} 
        {original.status !== SKIM_STATUS.END && <LotSkimWeightPopover
          title={"น้ำหนักบ่อสกิม"}
          target={original.id}
          onUpdate={() => refreshData()}
        >
          <Tooltip triger='hover' title={"กรอกน้ำหนัก"}>
            <EditOutlined
              style={{ color: colorWarning, marginLeft: 5 }}
            />
          </Tooltip>
        </LotSkimWeightPopover>}
      </div>
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      align: "center",
      sorter : true,
      render: (value) => value ? SKIM_STATUS_TH[value] : '-'
    },
    {
      title: "ผลแล็บ",
      dataIndex: "",
      key: "lab_results",
      render: (value, original) => {
        return _.get(original, "lab_results", "-").map((item) => {
          return (
            <div>
              <Tooltip title={item.note}>{item.chemical} : {toFixed(item.amount, 3)}</Tooltip>
            </div>
          );
        });
      },
    },
  ];

  return (
    <div>
      <ErrorMessages message={errorMessages}/>
      <ColorButton
        override={colorSuccess}
        type="primary"
        style={{ marginBottom: 10, float: "right"   }}
        icon={<PlusCircleFilled />}
        onClick={() => setOpenAddLotSkimModal(true)}
      >
        เพิ่มล็อตบ่อยางสกิม
      </ColorButton>

      <TSTable
        columns={columns}
        size="small"
        url={URL_SKIM.SKIM_TRANSACTION}
        // params={{ordering : 'status'}}
        ref={tableRef}
      />

      <AddLotSkimModal
        open={openAddLotSkimModal}
        onClose={() => {
          setOpenAddLotSkimModal(false);
          setTarget(null);
        }}
        onUpdate={() => {
          setOpenAddLotSkimModal(false);
          setTarget(null);
          refreshData();
        }}
        target={target}
      />

      <CloseLotSkimModal
        open={openCloseLotSkimModal}
        onClose={() => {
          setOpenCloseLotSkimModal(false);
          setTarget(null);
        }}
        onUpdate={() => {
          setOpenCloseLotSkimModal(false);
          setTarget(null);
          refreshData();
        }}
        target={target}
      />

      <CoagulationSkimModal
        open={openCoagulationSkimModal}
        onClose={() => {
          setOpenCoagulationSkimModal(false);
          setTarget(null);
        }}
        onUpdate={() => {
          setOpenCoagulationSkimModal(false);
          setTarget(null);
          refreshData();
        }}
        target={target}
      />

      <LotSkimHistoryModal
        open={openLotSkimHistoryModal}
        target={target}
        onClose={() => {
          setOpenLotSkimHistoryModal(false);
          setTarget(null);
        }}
        onUpdate={() => {
          setOpenLotSkimHistoryModal(false);
          setTarget(null);
          refreshData();
        }}
      />

      <SkimChemicalLogView
        open={skimChemicalLogTarget ? true : false}
        target={skimChemicalLogTarget}
        onClose={() => setSkimChemicalLogTarget(null)}
      />

    </div>
  );
}
